import {
    EnterpriseHandle,
    OrganizationHandle,
    OrganizationPublicHandle,
    OrganizationPrivateHandle,
    UserHandle,
} from "@buildwithflux/identifiers-schema/handles";
import {
    EnterpriseUid,
    isEnterpriseUid,
    isOrganizationUid,
    OrganizationUid,
    UserUid,
} from "@buildwithflux/identifiers-schema/legacy";

/**
 * Minimal structural account types for URL routing
 *
 * These are structural subsets of the corresponding types in `@buildwithflux/models` (`IUserData`, `Organization`,
 * `Enterprise`, `AccountWithDetails`, `WithAccountHandles`), containing only the fields that URL routing consumes.
 * The full model types are assignable to these, so callers can keep passing model objects.
 */

export interface UserForUrlRouting {
    uid: UserUid;
    handle: UserHandle;
}

export interface OrganizationForUrlRouting {
    uid: OrganizationUid;
    handle: OrganizationHandle;
    enterpriseUid?: EnterpriseUid | undefined;
}

export interface EnterpriseOrganizationForUrlRouting extends OrganizationForUrlRouting {
    enterpriseUid: EnterpriseUid;
}

export interface NonEnterpriseOrganizationForUrlRouting extends OrganizationForUrlRouting {
    enterpriseUid?: undefined;
}

export interface EnterpriseForUrlRouting {
    uid: EnterpriseUid;
    handle: EnterpriseHandle;
    defaultOrganizationUid: OrganizationUid;
}

export type AccountForUrlRouting = UserForUrlRouting | OrganizationForUrlRouting | EnterpriseForUrlRouting;

export type AccountWithDetailsForUrlRouting =
    | UserForUrlRouting
    | (NonEnterpriseOrganizationForUrlRouting & {enterprise?: never})
    | (EnterpriseOrganizationForUrlRouting & {enterprise: EnterpriseForUrlRouting})
    | EnterpriseForUrlRouting;

export type AccountPublicHandleForUrlRouting = UserHandle | OrganizationPublicHandle;

interface WithAccountPublicHandle {
    accountHandle: AccountPublicHandleForUrlRouting;
    enterpriseHandle?: never;
    enterpriseOrganizationHandle?: never;
}

interface WithEnterpriseHandles {
    accountHandle?: never;
    enterpriseHandle: EnterpriseHandle;
    enterpriseOrganizationHandle?: OrganizationPrivateHandle | undefined;
}

export type WithAccountHandlesForUrlRouting<T extends object> = T & (WithAccountPublicHandle | WithEnterpriseHandles);

export function accountIsEnterpriseForUrlRouting(
    account: Pick<AccountForUrlRouting, "uid">,
): account is EnterpriseForUrlRouting {
    return isEnterpriseUid(account.uid);
}

export function accountIsOrganizationForUrlRouting(
    account: Pick<AccountForUrlRouting, "uid">,
): account is OrganizationForUrlRouting {
    return isOrganizationUid(account.uid);
}

export function accountIsUserForUrlRouting(account: Pick<AccountForUrlRouting, "uid">): account is UserForUrlRouting {
    return !isOrganizationUid(account.uid) && !isEnterpriseUid(account.uid);
}

export function isEnterpriseOrganizationForUrlRouting(
    organization: OrganizationForUrlRouting | undefined,
): organization is EnterpriseOrganizationForUrlRouting {
    return !!organization && organization.enterpriseUid !== undefined;
}

export function isNonEnterpriseOrganizationForUrlRouting(
    organization: OrganizationForUrlRouting | undefined,
): organization is NonEnterpriseOrganizationForUrlRouting {
    return !!organization && organization.enterpriseUid === undefined;
}
